(() => {
  'use strict';

  class FocusTrap {
    constructor(root) {
      this.root = root;
      this.previouslyFocused = null;
      this.onKeydown = this.onKeydown.bind(this);
    }
    get focusables() {
      const selector = [
        'a[href]:not([disabled])',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      return Array.from(this.root.querySelectorAll(selector)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
    }
    activate() {
      this.previouslyFocused = document.activeElement;
      document.addEventListener('keydown', this.onKeydown);
      const first = this.focusables[0];
      if (first) {
        requestAnimationFrame(() => {
          first.classList.add('cdi-focus-suppress');
          first.focus({ preventScroll: true });
          first.addEventListener('blur', () => first.classList.remove('cdi-focus-suppress'), { once: true });
        });
      }
    }
    deactivate() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus();
      }
    }
    onKeydown(e) {
      if (e.key !== 'Tab') return;
      const focusables = this.focusables;
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  const scrollLockManager = window.CdiScrollLock || {
    lock() {},
    unlock() {},
    forceUnlock() {},
    getScrollY() { return window.scrollY; },
    debugOwners() { return []; },
  };
  (function() {
    const el = document.createElement('div');
    el.className = 'cdi-mega-backdrop';
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.4);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);z-index:95;opacity:0;visibility:hidden;pointer-events:none;transition:opacity 0.25s ease-out,visibility 0s linear 0.25s;';
    document.body.appendChild(el);
  }());
  const backdropManager = (() => {
    let activeKey = null;
    function getEl() { return document.querySelector('.cdi-mega-backdrop'); }
    return {
      show(key, onClose) {
        const el = getEl();
        if (!el) return;
        activeKey = key;
        el.style.top = '0';
        el.style.transition = 'opacity 0.25s ease-out, visibility 0s linear 0s';
        el.style.opacity = '1';
        el.style.visibility = 'visible';
        el.style.pointerEvents = 'none';
        el.onclick = null;
        requestAnimationFrame(() => {
          if (activeKey !== key) return;
          el.style.pointerEvents = 'auto';
          el.onclick = onClose || null;
        });
      },
      hide(key) {
        if (key && key !== activeKey) return;
        const el = getEl();
        if (!el) return;
        activeKey = null;
        el.style.transition = 'opacity 0.25s ease-out, visibility 0s linear 0.25s';
        el.style.opacity = '0';
        el.style.visibility = 'hidden';
        el.style.pointerEvents = 'none';
        el.onclick = null;
      },
      getActiveKey() { return activeKey; },
    };
  })();
  class CdiHeader extends HTMLElement {
    connectedCallback() {
      this.transparentEnabled = this.dataset.transparentEnabled === 'true';
      if (!this.transparentEnabled) {
        const heroEl = document.querySelector('[data-requests-transparent-header]');
        if (heroEl) {
          this.transparentEnabled = true;
          this.dataset.transparentEnabled = 'true';
          this.dataset.state = 'transparent';
          const heroStyle = heroEl.dataset.transparentStyle;
          if (heroStyle) this.dataset.style = heroStyle;
          const mainEl = document.querySelector('main.content-for-layout');
          if (mainEl) mainEl.dataset.transparentHero = 'true';
        }
      }
      this._isDocFlowHeader = document.body.dataset.headerMode === 'B';
      this._scrollRafPending = false;
      this.announcementEl = this._isDocFlowHeader
        ? null
        : document.querySelector('.cdi-announcement__bar');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => { this.syncHeight(); });
      });

      this.onResize = () => requestAnimationFrame(() => {
        this.syncHeight();
        if (this._buildTabsObserver) this._buildTabsObserver();
      });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.onPageShow = (e) => {
        if (!e.persisted) return;
        scrollLockManager.forceUnlock();
        this.classList.add('cdi-header--no-transition');
        this.syncHeight();
        if (!this._isDocFlowHeader) this._onScroll();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this._isDocFlowHeader && this._bfcacheScrollY != null) {
              window.scrollTo({ top: this._bfcacheScrollY, behavior: 'instant' });
            }
            this.classList.remove('cdi-header--no-transition');
          });
        });
      };
      window.addEventListener('pageshow', this.onPageShow);
      this.onPageHide = () => { this._bfcacheScrollY = window.scrollY; };
      window.addEventListener('pagehide', this.onPageHide);
      if (!this._isDocFlowHeader) {
        this.onScroll = () => {
          if (this._scrollRafPending) return;
          this._scrollRafPending = true;
          requestAnimationFrame(() => {
            this._scrollRafPending = false;
            this._onScroll();
          });
        };
        this._onScroll();
        window.addEventListener('scroll', this.onScroll, { passive: true });
        const tabsSentinel = document.querySelector('.cdi-content-tabs__sticky-sentinel');
        const activeSentinel = tabsSentinel;
        if (activeSentinel) {
          const headerEl = this;
          const updateRetractThreshold = () => {
            if (activeSentinel.dataset.retractThreshold === 'immediate') {
              headerEl._retractThreshold = 1;
              return;
            }
            let top = 0;
            let el = activeSentinel;
            while (el) { top += el.offsetTop; el = el.offsetParent; }
            headerEl._retractThreshold = top;
          };
          updateRetractThreshold();
          const origOnResize = this.onResize;
          this.onResize = () => { origOnResize.call(this); updateRetractThreshold(); };
          window.removeEventListener('resize', origOnResize);
          window.addEventListener('resize', this.onResize, { passive: true });
        }
      }
      this.bindIndicator();
    }
    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('pagehide', this.onPageHide);
    }
    syncHeight() {
      document.documentElement.style.setProperty(
        '--cdi-header-height-px',
        this.offsetHeight + 'px'
      );
    }
    _onScroll() {
      const y = scrollLockManager.getScrollY();
      if (this.announcementEl) {
        const annH = this.announcementEl.offsetHeight;
        const hidden = y > annH;
        if (this.dataset.announcementHidden !== String(hidden)) {
          this.dataset.announcementHidden = hidden;
          const container = document.querySelector('.shopify-section-group-header-group');
          if (container) container.dataset.announcementHidden = hidden;
          this.syncHeight();
        }
      }
      if (this.transparentEnabled) {
        const isTransparent = this.dataset.state === 'transparent';
        const next = isTransparent ? (y > 60 ? 'solid' : 'transparent')
                                   : (y < 12  ? 'transparent' : 'solid');
        if (this.dataset.state !== next) {
          this.dataset.state = next;
        }
      }
      if (this._retractThreshold !== undefined) {
        const shouldRetract = y >= this._retractThreshold;
        if (shouldRetract && !this.dataset.headerRetracted) {
          this.dataset.headerRetracted = 'true';
        } else if (!shouldRetract && this.dataset.headerRetracted) {
          delete this.dataset.headerRetracted;
        }
      }
    }
    bindIndicator() {
      const nav = this.querySelector('.cdi-header__nav');
      if (!nav) return;
      const links = nav.querySelectorAll('.cdi-header__nav-link');
      links.forEach((link) => {
        link.addEventListener('mouseenter', () => this.updateIndicator(link));
        link.addEventListener('focus', () => this.updateIndicator(link));
      });
      nav.addEventListener('mouseleave', () => {
        const megaMenu = this.querySelector('cdi-mega-menu');
        if (megaMenu && megaMenu.activeId) return;
        this.updateIndicator(null);
      });
      nav.addEventListener('focusout', (e) => {
        if (!nav.contains(e.relatedTarget)) this.updateIndicator(null);
      });
    }
    updateIndicator(trigger) {
      const indicator = this.querySelector('.cdi-header__nav-indicator');
      const inner = this.querySelector('.cdi-header__inner');
      if (!indicator || !inner) return;
      if (!trigger) {
        this._indicatorShown = false;
        indicator.style.opacity = '0';
        return;
      }
      const innerRect = inner.getBoundingClientRect();
      const label = Array.from(trigger.childNodes).find((n) => n.nodeType === 3 && n.textContent.trim());
      let labelRect;
      if (label) {
        const range = document.createRange();
        range.selectNodeContents(label);
        labelRect = range.getBoundingClientRect();
      } else {
        labelRect = trigger.getBoundingClientRect();
      }
      const x = labelRect.left - innerRect.left;
      const w = labelRect.width;
      const s = w / (indicator.offsetWidth || 1);
      const target = `translateX(${x}px) scaleX(${s})`;
      indicator.style.top = inner.offsetHeight - 2 + 'px';
      if (!this._indicatorShown) {
        indicator.style.transition = 'none';
        indicator.style.transform = `translateX(${x + w * 0.3}px) scaleX(${s * 0.4})`;
        void indicator.offsetWidth;
        indicator.style.transition = '';
        this._indicatorShown = true;
      }
      indicator.style.transform = target;
      indicator.style.opacity = '1';
    }
  }
  class CdiMegaMenu extends HTMLElement {
    connectedCallback() {
      this.CLOSE_DELAY = 150;
      this.INTENT_POLL_MS = 50;
      this.INTENT_SENSITIVITY_PX = 8;
      this.INTENT_MAX_WAIT_MS = 400;
      this.openTimer = null;
      this.closeTimer = null;
      this.activeId = null;
      this._intentState = null;
      this.triggers = this.querySelectorAll('[data-mega-target]');
      const header = this.closest('.cdi-header');
      this.panelsHost = header
        ? header.querySelector('.cdi-header__mega-panels')
        : null;
      if (this.panelsHost) this.panelsHost.removeAttribute('hidden');
      this.bindTriggers();
      this.bindPanels();
      this.bindSubPanels();
      this.bindKeyboard();
      this.onResize = this.onResize.bind(this);
      window.addEventListener('resize', this.onResize, { passive: true });
    }
    disconnectedCallback() {
      if (this.openTimer) clearTimeout(this.openTimer);
      if (this.closeTimer) clearTimeout(this.closeTimer);
      this._cancelIntent();
      window.removeEventListener('resize', this.onResize);
    }
    _startIntent(id, trigger, entryX, entryY) {
      this._cancelIntent();
      let pX = entryX, pY = entryY;
      let cX = entryX, cY = entryY;
      const startTime = performance.now();

      const onMove = (e) => { cX = e.clientX; cY = e.clientY; };
      document.addEventListener('mousemove', onMove, { passive: true });
      const poll = () => {
        const dist = Math.sqrt((pX - cX) ** 2 + (pY - cY) ** 2);
        const elapsed = performance.now() - startTime;
        if (dist < this.INTENT_SENSITIVITY_PX || elapsed >= this.INTENT_MAX_WAIT_MS) {
          document.removeEventListener('mousemove', onMove);
          this._intentState = null;
          this.openNow(id, trigger);
        } else {
          pX = cX; pY = cY;
          this._intentState.timer = setTimeout(poll, this.INTENT_POLL_MS);
        }
      };
      this._intentState = { timer: setTimeout(poll, this.INTENT_POLL_MS), onMove };
    }
    _cancelIntent() {
      if (!this._intentState) return;
      clearTimeout(this._intentState.timer);
      document.removeEventListener('mousemove', this._intentState.onMove);
      this._intentState = null;
    }
    _hydrateImages() {
      if (this._imagesHydrated || !this.panelsHost) return;
      this._imagesHydrated = true;
      this.panelsHost.querySelectorAll('template[data-cdi-mega-img]').forEach((tpl) => {
        const frag = tpl.content;
        frag.querySelectorAll('img').forEach((img) => { img.loading = 'eager'; });
        tpl.replaceWith(frag);
      });
    }
    bindTriggers() {
      this.triggers.forEach((trigger) => {
        const id = trigger.dataset.megaTarget;
        const item = trigger.closest('.cdi-header__nav-item');
        if (!item) return;
        item.addEventListener('mouseenter', (e) => {
          this._hydrateImages();
          this.scheduleOpen(id, trigger, e);
        });
        item.addEventListener('mouseleave', () => {
          this._cancelIntent();
          this.scheduleClose();
        });
        trigger.addEventListener('focus', () => {
          this._hydrateImages();
          this.scheduleOpen(id, trigger, null);
        });
      });
    }
    bindPanels() {
      if (!this.panelsHost) return;
      this.panelsHost.querySelectorAll('[data-mega-panel]').forEach((panel) => {
        panel.addEventListener('mouseenter', () => this.cancelClose());
        panel.addEventListener('mouseleave', () => this.scheduleClose());
      });
    }
    bindKeyboard() {
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.activeId) {
          e.preventDefault();
          this.closeNow();
          const active = this.querySelector(`[data-mega-target="${this.activeId}"]`);
          if (active) active.focus();
          return;
        }
        const trigger = e.target.closest && e.target.closest('[data-mega-target]');
        if (!trigger) return;
        const triggerArr = Array.from(this.triggers);
        const idx = triggerArr.indexOf(trigger);
        if (idx === -1) return;
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          const next = triggerArr[(idx + 1) % triggerArr.length];
          next.focus();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const prev = triggerArr[(idx - 1 + triggerArr.length) % triggerArr.length];
          prev.focus();
        } else if (e.key === 'ArrowDown' || e.key === 'Enter' && e.altKey) {
          const id = trigger.dataset.megaTarget;
          if (id) {
            e.preventDefault();
            this.openNow(id, trigger);
            const panel = this.panelsHost && this.panelsHost.querySelector(`[data-mega-panel="${id}"]`);
            if (panel) {
              const firstLink = panel.querySelector('a, button');
              if (firstLink) firstLink.focus();
            }
          }
        }
      });
    }
    scheduleOpen(id, trigger, mouseEvent) {
      const header = this.closest('cdi-header') || document.querySelector('.cdi-header');
      if (header && header.dataset.searchOpen === 'true') return;
      this.cancelClose();
      if (this.openTimer) clearTimeout(this.openTimer);
      if (this.activeId === id) return;
      if (!mouseEvent) {
        this.openNow(id, trigger);
        return;
      }
      this._startIntent(id, trigger, mouseEvent.clientX, mouseEvent.clientY);
    }
    openNow(id, trigger) {
      if (!this.panelsHost) {
        const header = this.closest('.cdi-header');
        this.panelsHost = header
          ? header.querySelector('.cdi-header__mega-panels')
          : null;
        if (this.panelsHost) this.panelsHost.removeAttribute('hidden');
      }
      if (!this.panelsHost) return;
      if (this.openTimer) {
        clearTimeout(this.openTimer);
        this.openTimer = null;
      }
      if (this.activeId && this.activeId !== id) {
        const prev = this.panelsHost.querySelector(`[data-mega-panel="${this.activeId}"]`);
        if (prev) {
          prev.dataset.open = 'false';
          if (!prev.classList.contains('cdi-header__mega--compact')) {
            CdiMegaMenu.hideBodyBackdrop();
          }
        }
      }
      const panel = this.panelsHost.querySelector(`[data-mega-panel="${id}"]`);
      if (!panel) return;
      this.positionCompact(panel, trigger);
      panel.dataset.open = 'true';
      this.panelsHost.dataset.open = 'true';
      const header = document.querySelector('cdi-header, .cdi-header');
      if (header) header.dataset.megaOpen = 'true';
      const isCompact = panel.classList.contains('cdi-header__mega--compact');
      if (!isCompact) {
        CdiMegaMenu.showBodyBackdrop(() => this.closeNow());
      }
      this.activeId = id;
      this.triggers.forEach((t) => {
        t.setAttribute('aria-expanded', t.dataset.megaTarget === id ? 'true' : 'false');
      });
      const headerEl2 = this.closest('cdi-header');
      if (headerEl2 && headerEl2.updateIndicator) headerEl2.updateIndicator(trigger);
      const headerEl = document.querySelector('cdi-header, .cdi-header');
      if (headerEl && headerEl._isDocFlowHeader) {
        this._scrollCloseHandler = () => { this.closeNow(); };
        window.addEventListener('scroll', this._scrollCloseHandler, { once: true, passive: true });
      }
    }
    scheduleClose() {
      if (this.closeTimer) clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeNow(), this.CLOSE_DELAY);
    }
    cancelClose() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }
    closeNow() {
      this._cancelIntent();
      if (this._scrollCloseHandler) {
        window.removeEventListener('scroll', this._scrollCloseHandler);
        this._scrollCloseHandler = null;
      }
      if (!this.panelsHost) return;
      this.panelsHost.querySelectorAll('[data-mega-panel]').forEach((p) => {
        p.dataset.open = 'false';
      });
      this.panelsHost.dataset.open = 'false';
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      this.activeId = null;
      const header = document.querySelector('cdi-header, .cdi-header');
      if (header) header.dataset.megaOpen = 'false';
      CdiMegaMenu.hideBodyBackdrop();
      const headerEl = this.closest('cdi-header');
      if (headerEl && headerEl.updateIndicator) headerEl.updateIndicator(null);
    }
    static showBodyBackdrop(onClose) {
      const el = document.querySelector('.cdi-mega-backdrop');
      if (!el) return;
      backdropManager.show('mega', onClose);
      const headerEl = document.querySelector('cdi-header');
      const h = headerEl ? Math.max(0, headerEl.getBoundingClientRect().bottom) : 72;
      el.style.top = h + 'px';
      el.style.pointerEvents = 'none';
    }
    static hideBodyBackdrop() {
      if (backdropManager.getActiveKey() !== 'mega') return;
      backdropManager.hide('mega');
    }
    positionCompact(panel, trigger) {
      if (!panel.classList.contains('cdi-header__mega--compact')) {
        panel.style.left = '';
        panel.style.right = '';
        return;
      }
      const triggerRect = trigger.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const panelWidth = panel.offsetWidth || 180;
      const wouldOverflow = triggerRect.left + panelWidth > viewportWidth - 16;
      if (wouldOverflow) {
        panel.style.left = 'auto';
        panel.style.right = Math.max(16, viewportWidth - triggerRect.right) + 'px';
      } else {
        panel.style.right = 'auto';
        panel.style.left = triggerRect.left + 'px';
      }
    }
    bindSubPanels() {
      if (!this.panelsHost) return;
      const SUB_DELAY = 80;
      this.panelsHost.querySelectorAll('[data-mega-sub]').forEach((link) => {
        if (link.closest('.cdi-header__mega-cards--solutions') ||
            link.closest('[data-mega-panel]')?.querySelector('.cdi-header__mega-cards--solutions')) {
          return;
        }
        let subTimer = null;
        const activate = () => {
          clearTimeout(subTimer);
          subTimer = setTimeout(() => {
            this._activateSubPanel(link);
          }, SUB_DELAY);
        };
        link.addEventListener('mouseenter', activate);
        link.addEventListener('focus', activate);
        link.addEventListener('mouseleave', () => clearTimeout(subTimer));
      });
      this.bindSubHighlight();
    }
    _activateSubPanel(activeLink) {
      const subId = activeLink.dataset.megaSub;
      if (!subId || !this.panelsHost) return;
      const megaPanel = activeLink.closest('[data-mega-panel]');
      if (!megaPanel) return;
      megaPanel.querySelectorAll('[data-mega-sub-panel]').forEach((sp) => { sp.dataset.active = 'false'; });
      megaPanel.querySelectorAll('[data-mega-sub]').forEach((lk) => { lk.setAttribute('aria-current', 'false'); });
      const target = megaPanel.querySelector(`[data-mega-sub-panel="${subId}"]`);
      if (target) target.dataset.active = 'true';
      activeLink.setAttribute('aria-current', 'true');
    }
    bindSubHighlight() {
      if (!this.panelsHost) return;
      this.panelsHost.querySelectorAll('[data-mega-panel]').forEach((megaPanel) => {
        const cardsGrid = megaPanel.querySelector('.cdi-header__mega-cards--solutions');
        if (!cardsGrid) return;
        const sidebar = megaPanel.querySelector('.cdi-header__mega-sidebar');
        if (!sidebar) return;
        const SUB_DELAY = 80;
        sidebar.querySelectorAll('[data-mega-sub]').forEach((link) => {
          let highlightTimer = null;
          const activate = () => {
            clearTimeout(highlightTimer);
            highlightTimer = setTimeout(() => {
              const subId = link.dataset.megaSub;
              cardsGrid.dataset.subActive = 'true';
              cardsGrid.querySelectorAll('[data-mega-sub-card]').forEach((card) => { delete card.dataset.active; });
              const target = cardsGrid.querySelector(`[data-mega-sub-card="${subId}"]`);
              if (target) target.dataset.active = 'true';
              sidebar.querySelectorAll('[data-mega-sub]').forEach((lk) => { lk.setAttribute('aria-current', 'false'); });
              link.setAttribute('aria-current', 'true');
            }, SUB_DELAY);
          };
          const deactivate = () => { clearTimeout(highlightTimer); };
          link.addEventListener('mouseenter', activate);
          link.addEventListener('focus', activate);
          link.addEventListener('mouseleave', deactivate);
        });
        sidebar.addEventListener('mouseleave', () => {
          delete cardsGrid.dataset.subActive;
          cardsGrid.querySelectorAll('[data-mega-sub-card]').forEach((card) => { delete card.dataset.active; });
          sidebar.querySelectorAll('[data-mega-sub]').forEach((lk) => { lk.removeAttribute('aria-current'); });
        });
      });
    }
    onResize() {
      if (this.activeId) {
        const trigger = this.querySelector(`[data-mega-target="${this.activeId}"]`);
        const panel = this.panelsHost && this.panelsHost.querySelector(`[data-mega-panel="${this.activeId}"]`);
        if (trigger && panel) this.positionCompact(panel, trigger);
        const headerEl = this.closest('cdi-header');
        if (trigger && headerEl && headerEl.updateIndicator) headerEl.updateIndicator(trigger);
      }
    }
  }
  class CdiDrawer extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('.cdi-header__drawer');
      this._drawerOriginalParent = this.drawer ? this.drawer.parentNode : null;
      this.toggle = document.querySelector('[data-drawer-toggle]');
      this.header = this.closest('cdi-header') || document.querySelector('.cdi-header');
      this.focusTrap = this.drawer ? new FocusTrap(this.drawer) : null;
      if (this.drawer) document.body.appendChild(this.drawer);
      if (this.toggle) {
        this.toggle.addEventListener('click', () => this.toggleDrawer());
      }
      this.bindAccordions();
      this.bindViewportChange();
      this.bindEscape();
    }
    disconnectedCallback() {
      if (this._drawerOriginalParent && this.drawer && !this.drawer.isConnected) {
        this._drawerOriginalParent.appendChild(this.drawer);
      }
      if (this.desktopQuery && this.handleViewportChange) {
        this.desktopQuery.removeEventListener('change', this.handleViewportChange);
      }
      if (this._onEscapeKeydown) {
        document.removeEventListener('keydown', this._onEscapeKeydown);
      }
    }
    toggleDrawer() {
      if (!this.drawer) return;
      const isOpen = this.drawer.dataset.open === 'true';
      if (isOpen) this.close();
      else this.open();
    }
    open() {
      if (!this.drawer) return;
      if (this.header) {
        const bottom = Math.max(0, this.header.getBoundingClientRect().bottom);
        this.drawer.style.setProperty('--cdi-drawer-top', bottom + 'px');
      }
      scrollLockManager.lock('header-drawer');
      this.drawer.dataset.open = 'true';
      if (this.header) this.header.dataset.drawerOpen = 'true';
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
      backdropManager.show('drawer', () => this.close());
      if (this.focusTrap) this.focusTrap.activate();
    }
    close() {
      if (!this.drawer) return;
      this.drawer.dataset.open = 'false';
      if (this.header) this.header.dataset.drawerOpen = 'false';
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
      scrollLockManager.unlock('header-drawer');
      backdropManager.hide('drawer');
      this.drawer.querySelectorAll('[data-drawer-accordion]').forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
        const submenu = btn.nextElementSibling;
        if (submenu) submenu.dataset.open = 'false';
      });
    }
    bindAccordions() {
      if (!this.drawer) return;
      this.drawer.querySelectorAll('[data-drawer-accordion]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const wasExpanded = btn.getAttribute('aria-expanded') === 'true';
          this.drawer.querySelectorAll('[data-drawer-accordion][aria-expanded="true"]').forEach((other) => {
            if (other !== btn) {
              other.setAttribute('aria-expanded', 'false');
              if (other.nextElementSibling) other.nextElementSibling.dataset.open = 'false';
            }
          });
          btn.setAttribute('aria-expanded', String(!wasExpanded));
          const submenu = btn.nextElementSibling;
          if (submenu) submenu.dataset.open = String(!wasExpanded);
        });
      });
    }
    bindViewportChange() {
      this.desktopQuery = window.matchMedia('(min-width: 1200px)');
      this.handleViewportChange = (e) => {
        if (e.matches && this.drawer && this.drawer.dataset.open === 'true') {
          this.close();
        }
      };
      this.desktopQuery.addEventListener('change', this.handleViewportChange);
    }
    bindEscape() {
      this._onEscapeKeydown = (e) => {
        if (e.key === 'Escape' && this.drawer && this.drawer.dataset.open === 'true') {
          this.close();
        }
      };
      document.addEventListener('keydown', this._onEscapeKeydown);
    }
  }
  class CdiSearchModal extends HTMLElement {
    connectedCallback() {
      this.modal = document.getElementById('cdi-search-panel');
      this._modalOriginalParent = this.modal ? this.modal.parentNode : null;
      if (this.modal) document.body.appendChild(this.modal);
      this.input = this.modal ? this.modal.querySelector('.cdi-header__search-input') : null;
      this.closeBtn = this.modal ? this.modal.querySelector('[data-search-close]') : null;
      this.panelInner = this.modal ? this.modal.querySelector('.cdi-header__search-panel-inner') : null;
      this.resultsEl = this.modal ? this.modal.querySelector('.cdi-search-results') : null;
      this.viewAllEl = this.modal ? this.modal.querySelector('#cdi-search-view-all') : null;
      this.toggles = Array.from(document.querySelectorAll('[data-search-toggle]'));
      this.header = document.querySelector('cdi-header, .cdi-header');
      this.focusTrap = this.modal ? new FocusTrap(this.modal) : null;
      this.searchCache = {};
      this.abortController = null;
      this.debounceTimer = null;
      this.toggles.forEach((btn) => {
        btn.addEventListener('click', () => this.open());
      });
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }
      if (this.input && this.panelInner) {
        this.input.addEventListener('input', () => {
          const query = this.input.value.trim();
          this.panelInner.classList.toggle('has-query', query.length > 0);
          clearTimeout(this.debounceTimer);
          if (!query) {
            this.clearResults();
            return;
          }
          this.debounceTimer = setTimeout(() => this.fetchResults(query), 300);
        });
      }
      this.bindEscape();
    }
    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this._modalOriginalParent && this.modal && !this.modal.isConnected) {
        this._modalOriginalParent.appendChild(this.modal);
      }
      if (this._onEscapeKeydown) {
        document.removeEventListener('keydown', this._onEscapeKeydown);
      }
    }

    async fetchResults(query) {
      if (this.searchCache[query]) {
        this.renderResults(this.searchCache[query], query);
        return;
      }
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      try {
        const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&section_id=cdi-predictive-search`;
        const res = await fetch(url, { signal: this.abortController.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const items = Array.from(doc.querySelectorAll('[data-product-item]'));
        this.searchCache[query] = items.map((el) => ({
          url: el.dataset.url,
          title: el.dataset.title,
          image: el.dataset.image || '',
        }));

        this.renderResults(this.searchCache[query], query);
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('Predictive search error:', e);
          this.renderNoResults();
        }
      }
    }
    renderResults(products, query) {
      if (!this.resultsEl) return;
      if (!products || !products.length) {
        this.renderNoResults();
        return;
      }
      const maxDisplay = 5;
      const displayed = products.slice(0, maxDisplay);
      const currentQuery = query || (this.input ? this.input.value.trim() : '');
      const itemsHtml = displayed
        .map((p) => {
          const thumb = p.image
            ? `<img src="${this.escapeHtml(p.image)}" alt="" loading="lazy" width="48" height="48" class="cdi-search-result__img">`
            : `<span class="cdi-search-result__placeholder"></span>`;
          return `<li class="cdi-search-result" role="option">
            <a href="${this.escapeHtml(p.url)}">
              ${thumb}
              <span class="cdi-search-result__title">${this.escapeHtml(p.title)}</span>
            </a>
          </li>`;
        })
        .join('');
      this.resultsEl.innerHTML = itemsHtml;
      if (this.viewAllEl) {
        const label = this.viewAllEl.dataset.viewAllLabel || 'View all results';
        this.viewAllEl.innerHTML = currentQuery
          ? `<a href="/search?q=${encodeURIComponent(currentQuery)}&type=product">${label}</a>`
          : '';
      }
    }

    renderNoResults() {
      if (!this.resultsEl) return;
      this.resultsEl.innerHTML = '<li class="cdi-search-no-results">No results found</li>';
    }
    clearResults() {
      if (this.resultsEl) this.resultsEl.innerHTML = '';
      if (this.viewAllEl) this.viewAllEl.innerHTML = '';
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
    open() {
      if (!this.modal) return;
      ensureAsyncCssApplied();
      if (this.header && window.innerWidth >= 1200) {
        const bottom = Math.max(0, this.header.getBoundingClientRect().bottom);
        this.modal.style.setProperty('--cdi-search-top', bottom + 'px');
      }
      this.modal.dataset.open = 'true';
      this.modal.setAttribute('aria-hidden', 'false');
      this.toggles.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      scrollLockManager.lock('search-modal');
      if (this.header) this.header.dataset.searchOpen = 'true';
      backdropManager.show('search', () => this.close());
      if (this.input) requestAnimationFrame(() => this.input.focus());
      if (this.focusTrap) this.focusTrap.activate();
    }

    close() {
      if (!this.modal) return;
      this.modal.dataset.open = 'false';
      this.modal.setAttribute('aria-hidden', 'true');
      this.toggles.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      scrollLockManager.unlock('search-modal');
      if (this.header) this.header.dataset.searchOpen = 'false';
      backdropManager.hide('search');
      if (this.focusTrap) this.focusTrap.deactivate();

      const closingProp = window.innerWidth < 1200 ? 'transform' : 'clip-path';
      let resetDone = false;
      const resetContent = () => {
        if (resetDone) return;
        resetDone = true;
        if (this.panelInner) this.panelInner.classList.remove('has-query');
        if (this.input) this.input.value = '';
        this.clearResults();
      };
      const onTransitionEnd = (e) => {
        if (e.target !== this.modal) return;
        if (e.propertyName === closingProp) {
          this.modal.removeEventListener('transitionend', onTransitionEnd);
          resetContent();
        }
      };
      this.modal.addEventListener('transitionend', onTransitionEnd);
      setTimeout(resetContent, 400);
    }
    bindEscape() {
      this._onEscapeKeydown = (e) => {
        if (e.key === 'Escape' && this.modal && this.modal.dataset.open === 'true') {
          this.close();
        }
      };
      document.addEventListener('keydown', this._onEscapeKeydown);
    }
  }

  function ensureAsyncCssApplied() {
    document.querySelectorAll('link[data-cdi-async-css][media="print"]').forEach((link) => {
      link.media = 'all';
    });
  }
  if (!customElements.get('cdi-header')) customElements.define('cdi-header', CdiHeader);
  if (!customElements.get('cdi-mega-menu')) customElements.define('cdi-mega-menu', CdiMegaMenu);
  if (!customElements.get('cdi-drawer')) customElements.define('cdi-drawer', CdiDrawer);
  if (!customElements.get('cdi-search-modal')) customElements.define('cdi-search-modal', CdiSearchModal);
  const firstInteractionCss = () => {
    ensureAsyncCssApplied();
    window.removeEventListener('pointerover', firstInteractionCss);
    window.removeEventListener('focusin', firstInteractionCss);
    window.removeEventListener('touchstart', firstInteractionCss);
  };
  window.addEventListener('pointerover', firstInteractionCss, { passive: true, once: true });
  window.addEventListener('focusin', firstInteractionCss, { once: true });
  window.addEventListener('touchstart', firstInteractionCss, { passive: true, once: true });
})();
